import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { useAuthContext } from '../contexts/AuthContext';
import { isOrgManager } from '../lib/orgRoles';
import api from '../lib/api';
import { getApiErrorMessage } from '../lib/apiErrors';
import { getApiBaseUrlForBrowserFetch, PREVIEW_PR } from '../lib/previewApiLookup';
import { TextSessionView } from '../components/practice/TextSessionView';
import { VerbalSessionView } from '../components/practice/VerbalSessionView';
import type {
  Message,
  Evaluation,
  SessionInfo,
  SessionUsage,
  Participant,
  RecordingVisibility,
  SessionViewer,
} from '../components/practice/types';
import { RecordingAccessControl } from '../components/practice/RecordingAccessControl';
import { RecordingPrivateNotice } from '../components/practice/RecordingPrivateNotice';

const NO_VIEWERS: SessionViewer[] = [];
import { useSessionStream } from '../hooks/useSessionStream';
import { appendMessageDeduped, mergeStreamMessage } from './practiceMessageMerge';
import { OVERRUN_GRACE_SECONDS } from '../components/practice/formatTimer';

interface OrgMember {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

function sessionInviteSkipStorageKey(sessionId: string) {
  return `practiceSessionInviteSkip:${sessionId}`;
}

function parseJoinDeniedPayload(error: unknown): { visibility?: string; canJoin?: boolean } | null {
  let payload: unknown;
  if (axios.isAxiosError(error) && error.response?.status === 403) {
    payload = error.response.data;
  } else if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    (error as { response?: { status?: number } }).response?.status === 403
  ) {
    payload = (error as { response: { data: unknown } }).response.data;
  } else {
    return null;
  }
  if (payload && typeof payload === 'object' && payload !== null && 'canJoin' in payload) {
    return payload as { visibility?: string; canJoin?: boolean };
  }
  return null;
}

export function PracticeSessionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [ending, setEnding] = useState(false);
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const { user } = useAuthContext();
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [sessionUsage, setSessionUsage] = useState<SessionUsage | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [speakingUsers, setSpeakingUsers] = useState<Set<string>>(new Set());
  const [accessDenied, setAccessDenied] = useState<{
    visibility?: string;
    canJoin?: boolean;
  } | null>(null);
  const [joining, setJoining] = useState(false);
  const [orgFeatures, setOrgFeatures] = useState<string[] | null>(null);
  const [showTranscriptByDefault, setShowTranscriptByDefault] = useState<boolean | undefined>(
    undefined,
  );
  const [showPersonaFaces, setShowPersonaFaces] = useState(true);
  const [orgMeLoaded, setOrgMeLoaded] = useState(false);
  const [visibilityUpdating, setVisibilityUpdating] = useState(false);
  const [visibilityError, setVisibilityError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [inviteEmailError, setInviteEmailError] = useState<string | null>(null);
  const [inviteEmailMessage, setInviteEmailMessage] = useState<string | null>(null);
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [inviteUserIds, setInviteUserIds] = useState<string[]>([]);
  const [sendInviteToSelf, setSendInviteToSelf] = useState(false);
  const [inviteSending, setInviteSending] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  /** Reconcile optimistic user row with persisted `message_added` from the session stream (text + verbal). */
  const pendingOptimisticUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    api
      .get('/orgs/me')
      .then((r) => {
        setOrgFeatures(r.data.features ?? []);
        setShowTranscriptByDefault(Boolean(r.data.showTranscriptByDefault));
        setShowPersonaFaces(r.data.showPersonaFaces ?? true);
      })
      .catch(() => {
        setOrgFeatures([]);
        setShowTranscriptByDefault(false);
        setShowPersonaFaces(true);
      })
      .finally(() => setOrgMeLoaded(true));
  }, []);

  useEffect(() => {
    if (!id) return;
    const st = location.state as
      | { inviteError?: string; justCreatedWithInvitesSent?: boolean }
      | null
      | undefined;
    if (st?.justCreatedWithInvitesSent) {
      try {
        sessionStorage.setItem(sessionInviteSkipStorageKey(id), '1');
      } catch {
        // ignore
      }
      setInviteEmailMessage('Invite emails were sent.');
      navigate('.', { replace: true, state: null });
    }
    if (st?.inviteError) {
      setInviteEmailError(st.inviteError);
      navigate('.', { replace: true, state: null });
    }
  }, [id, location.state, navigate]);

  const loadSession = useCallback(async () => {
    if (!id || id === 'new') return;
    try {
      const res = await api.get(`/practice/sessions/${id}`);
      setSessionInfo(res.data.session);
      setMessages(res.data.messages);
      setParticipants(res.data.session.participants ?? []);
      setSessionUsage(res.data.usage ?? null);
      setAccessDenied(null);
      if (res.data.evaluation) {
        setEvaluation(res.data.evaluation);
      }
    } catch (err: unknown) {
      const data = parseJoinDeniedPayload(err);
      if (data?.canJoin !== undefined) {
        setAccessDenied({
          visibility: data.visibility,
          canJoin: data.canJoin,
        });
      }
    }
  }, [id]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const canMultiUser = Boolean(orgFeatures?.includes('multi_user'));
  const isCreator = Boolean(user?.id) && String(sessionInfo?.userId) === String(user?.id);
  const canManageAsOrgManager = isOrgManager(user?.orgRole);
  const canManageSessionInvites =
    canMultiUser &&
    sessionInfo?.visibility === 'org_open' &&
    sessionInfo?.status === 'active' &&
    (isCreator || canManageAsOrgManager);

  useEffect(() => {
    if (!id || !canManageSessionInvites) return;
    let cancelled = false;
    void api
      .get<OrgMember[]>('/orgs/me/members')
      .then((res) => {
        if (!cancelled) setOrgMembers(res.data);
      })
      .catch(() => {
        if (!cancelled) setOrgMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [id, canManageSessionInvites]);

  const isParticipant = sessionInfo?.isParticipant ?? false;

  const { postSpeaking } = useSessionStream(id, isParticipant && sessionInfo?.status === 'active', {
    onMessage: useCallback(
      (msg: Message) => {
        setMessages((prev) => {
          const { next, clearedPending } = mergeStreamMessage(prev, msg, {
            pendingOptimisticUserId: pendingOptimisticUserIdRef.current,
            currentUserId: user?.id,
          });
          if (clearedPending) {
            pendingOptimisticUserIdRef.current = null;
          }
          return next;
        });
      },
      [user?.id],
    ),
    onParticipantJoined: useCallback((participant: Participant) => {
      setParticipants((prev) => {
        if (prev.some((p) => p.userId === participant.userId)) return prev;
        return [...prev, participant];
      });
    }, []),
    onSessionEnded: useCallback(() => {
      setSessionInfo((prev) => (prev ? { ...prev, status: 'completed' } : prev));
    }, []),
    onSpeakingStarted: useCallback((userId: string) => {
      setSpeakingUsers((prev) => new Set(prev).add(userId));
    }, []),
    onSpeakingStopped: useCallback((userId: string) => {
      setSpeakingUsers((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }, []),
    onError: useCallback((error: Error) => {
      setStreamError(error.message || 'Failed to connect to session stream.');
    }, []),
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const isCompleted = sessionInfo?.status === 'completed';
  const isOwnSession = !sessionInfo || sessionInfo.userId === user?.id;

  const handleEndSession = useCallback(async () => {
    setEnding(true);
    try {
      const res = await api.post(`/practice/sessions/${id}/end`);
      setSessionInfo((prev) => (prev ? { ...prev, status: 'completed' } : prev));
      if (res.data.evaluation) {
        setEvaluation(res.data.evaluation);
      }
    } catch {
      // best-effort
    } finally {
      setEnding(false);
    }
  }, [id]);

  const handleJoin = useCallback(async () => {
    if (!id) return;
    setJoining(true);
    try {
      await api.post(`/practice/sessions/${id}/join`);
      await loadSession();
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      setJoining(false);
    }
  }, [id, loadSession]);

  const handleVisibilityChange = useCallback(
    async (visibility: 'private' | 'org_open') => {
      if (!id) return;
      setVisibilityUpdating(true);
      setVisibilityError(null);
      try {
        await api.patch(`/practice/sessions/${id}`, { visibility });
        setSessionInfo((prev) => (prev ? { ...prev, visibility } : null));
      } catch (err: unknown) {
        Sentry.captureException(err);
        setVisibilityError(getApiErrorMessage(err));
      } finally {
        setVisibilityUpdating(false);
      }
    },
    [id],
  );

  const handleRecordingAccessSaved = useCallback(
    (recordingVisibility: RecordingVisibility, viewers: SessionViewer[]) => {
      setSessionInfo((prev) => (prev ? { ...prev, recordingVisibility, viewers } : prev));
    },
    [],
  );

  const toggleSessionInviteMember = (memberId: string) => {
    setInviteUserIds((prev) =>
      prev.includes(memberId) ? prev.filter((x) => x !== memberId) : [...prev, memberId],
    );
  };

  const handleSendSessionInvites = async () => {
    if (!id) return;
    if (inviteUserIds.length === 0 && !sendInviteToSelf) {
      setInviteEmailError('Select at least one teammate or send to yourself.');
      return;
    }
    setInviteSending(true);
    setInviteEmailError(null);
    setInviteEmailMessage(null);
    try {
      const res = await api.post(`/practice/sessions/${id}/invite-email`, {
        userIds: inviteUserIds,
        includeSelf: sendInviteToSelf,
      });
      const sent = (res.data as { sent: number }).sent;
      setInviteEmailMessage(`Sent ${sent} invite(s).`);
      setSessionInfo((prev) =>
        prev && sent > 0 ? { ...prev, inviteEmailsSentAt: new Date().toISOString() } : prev,
      );
    } catch (err: unknown) {
      setInviteEmailError(getApiErrorMessage(err));
      Sentry.captureException(err);
    } finally {
      setInviteSending(false);
    }
  };

  const copySessionLink = useCallback(async () => {
    if (!id) return;
    const url = `${window.location.origin}/practice/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [id]);

  const sendMessage = useCallback(
    async (content: string) => {
      const optimisticId = `opt-${Date.now()}`;
      pendingOptimisticUserIdRef.current = optimisticId;
      setMessages((prev) => [
        ...prev,
        {
          id: optimisticId,
          role: 'user',
          content,
          userId: user?.id ?? null,
          userName: user?.name ?? null,
          createdAt: new Date().toISOString(),
        },
      ]);
      setLoading(true);

      try {
        const res = await api.post(`/practice/sessions/${id}/messages`, { content });
        const incoming: Message[] = Array.isArray(res.data.messages)
          ? res.data.messages
          : [res.data];
        setMessages((prev) => {
          const withoutOptimistic = prev.filter((m) => m.id !== optimisticId);
          const existingIds = new Set(withoutOptimistic.map((m) => m.id));
          const newMsgs = incoming.filter((m) => !existingIds.has(m.id));
          return [...withoutOptimistic, ...newMsgs];
        });
        if (res.data.usage) {
          setSessionUsage(res.data.usage);
        }
      } catch {
        pendingOptimisticUserIdRef.current = null;
        setMessages((prev) => [
          ...prev.filter((m) => m.id !== optimisticId),
          {
            id: Date.now().toString(),
            role: 'assistant',
            content: 'Something went wrong. Please try again.',
            createdAt: new Date().toISOString(),
          },
        ]);
      } finally {
        pendingOptimisticUserIdRef.current = null;
        setLoading(false);
      }
    },
    [id, user],
  );

  const sendMessageSSE = useCallback(
    async (content: string) => {
      const optimisticId = `opt-${Date.now()}`;
      pendingOptimisticUserIdRef.current = optimisticId;
      setMessages((prev) => [
        ...prev,
        {
          id: optimisticId,
          role: 'user',
          content,
          userId: user?.id ?? null,
          userName: user?.name ?? null,
          createdAt: new Date().toISOString(),
        },
      ]);
      setLoading(true);

      await Sentry.startSpan(
        {
          name: 'practice.turn_sse',
          op: 'ui.action',
          attributes: { 'practice.session_id': id ?? '' },
        },
        async () => {
          try {
            const baseUrl = await getApiBaseUrlForBrowserFetch();
            const token = sessionStorage.getItem('token');
            const headers: Record<string, string> = {
              'Content-Type': 'application/json',
              Accept: 'text/event-stream',
            };
            if (token) headers['Authorization'] = `Bearer ${token}`;
            if (PREVIEW_PR) headers['X-Preview-PR'] = PREVIEW_PR;

            const fetchRes = await fetch(`${baseUrl}/practice/sessions/${id}/messages`, {
              method: 'POST',
              headers,
              body: JSON.stringify({ content }),
            });

            if (!fetchRes.ok || !fetchRes.body) {
              throw new Error('SSE request failed');
            }

            const reader = fetchRes.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let currentEvent = '';

            for (;;) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });

              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              for (const line of lines) {
                if (line.startsWith('event: ')) {
                  currentEvent = line.slice(7).trim();
                } else if (line.startsWith('data: ')) {
                  const payload = line.slice(6);
                  try {
                    const parsed = JSON.parse(payload);
                    if (currentEvent === 'done') {
                      if (parsed.usage) setSessionUsage(parsed.usage);
                    } else {
                      setMessages((prev) => appendMessageDeduped(prev, parsed as Message));
                    }
                  } catch {
                    // malformed JSON line, skip
                  }
                  currentEvent = '';
                }
              }
            }
          } catch (err) {
            Sentry.captureException(err, {
              tags: { subsystem: 'send_message_sse', sessionId: id },
            });
            const optId = pendingOptimisticUserIdRef.current;
            pendingOptimisticUserIdRef.current = null;
            setMessages((prev) => {
              const withoutOpt = optId ? prev.filter((m) => m.id !== optId) : prev;
              return appendMessageDeduped(withoutOpt, {
                id: `${Date.now()}`,
                role: 'assistant',
                content: 'Something went wrong. Please try again.',
                createdAt: new Date().toISOString(),
              });
            });
          } finally {
            setLoading(false);
          }
        },
      );
    },
    [id, user],
  );

  // Countdown timer
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const timerExpiredRef = useRef(false);

  useEffect(() => {
    const sessionStart = sessionInfo?.startedAt ?? sessionInfo?.createdAt;
    if (sessionInfo?.timeLimitMinutes == null || !sessionStart || isCompleted) {
      setRemainingSeconds(null);
      return;
    }

    const deadline = new Date(sessionStart).getTime() + sessionInfo.timeLimitMinutes * 60_000;

    const tick = () => {
      const left = Math.max(-OVERRUN_GRACE_SECONDS, Math.round((deadline - Date.now()) / 1000));
      setRemainingSeconds(left);
      if (left <= 0 && !timerExpiredRef.current) {
        timerExpiredRef.current = true;
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [sessionInfo?.timeLimitMinutes, sessionInfo?.startedAt, sessionInfo?.createdAt, isCompleted]);

  useEffect(() => {
    if (
      remainingSeconds !== null &&
      remainingSeconds <= -OVERRUN_GRACE_SECONDS &&
      timerExpiredRef.current &&
      !isCompleted &&
      !ending &&
      isOwnSession
    ) {
      handleEndSession();
    }
  }, [remainingSeconds, isCompleted, ending, isOwnSession, handleEndSession]);

  if (accessDenied) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          {accessDenied.canJoin ? (
            <>
              <p className="text-gray-400 mb-4">This session is open to your organization.</p>
              <button
                type="button"
                onClick={handleJoin}
                disabled={joining}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {joining ? 'Joining...' : 'Join Session'}
              </button>
            </>
          ) : accessDenied.visibility === 'org_open' ? (
            <p className="text-gray-400 mb-4 max-w-md mx-auto">
              This session is shared with your organization, but joining requires an Enterprise plan
              that includes multi-user practice.
            </p>
          ) : (
            <p className="text-gray-400">This session is private.</p>
          )}
          <button
            onClick={() => navigate('/practice')}
            className="mt-4 block mx-auto text-sm text-gray-500 hover:text-gray-700"
          >
            Back to sessions
          </button>
        </div>
      </div>
    );
  }

  if (!sessionInfo) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading session...</div>
      </div>
    );
  }

  if (sessionInfo.mode === 'verbal' && !orgMeLoaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading session...</div>
      </div>
    );
  }

  const sharedProps = {
    sessionInfo,
    messages,
    evaluation,
    loading,
    isCompleted: !!isCompleted,
    isOwnSession,
    remainingSeconds,
    ending,
    sessionUsage,
    participants,
    speakingUsers,
    onSendMessage: sendMessage,
    onEndSession: handleEndSession,
    onBack: () => navigate('/practice'),
    postSpeaking,
    showTranscriptByDefault,
    showPersonaFaces,
  };

  const isCreatorForToolbar = Boolean(user?.id) && String(sessionInfo.userId) === String(user?.id);
  const canManageVisibility = canMultiUser && (isCreatorForToolbar || canManageAsOrgManager);
  const showCopyLink =
    canMultiUser && sessionInfo.visibility === 'org_open' && sessionInfo.status === 'active';

  const streamErrorBanner = streamError ? (
    <div
      className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 flex flex-wrap items-center justify-between gap-2"
      role="alert"
    >
      <span>Live session updates unavailable: {streamError}</span>
      <button
        type="button"
        onClick={() => setStreamError(null)}
        className="text-red-800 underline shrink-0"
      >
        Dismiss
      </button>
    </div>
  ) : null;

  const inviteEmailBanner = inviteEmailError ? (
    <div
      className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex flex-wrap items-center justify-between gap-2"
      role="alert"
    >
      <span>Invite emails could not be sent: {inviteEmailError}</span>
      <button
        type="button"
        onClick={() => setInviteEmailError(null)}
        className="text-amber-800 underline shrink-0"
      >
        Dismiss
      </button>
    </div>
  ) : null;

  const inviteEmailSuccessBanner = inviteEmailMessage ? (
    <div
      className="mb-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900 flex flex-wrap items-center justify-between gap-2"
      role="status"
    >
      <span>{inviteEmailMessage}</span>
      <button
        type="button"
        onClick={() => setInviteEmailMessage(null)}
        className="text-green-800 underline shrink-0"
      >
        Dismiss
      </button>
    </div>
  ) : null;

  const sessionInviteSkipFlag =
    id && typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(sessionInviteSkipStorageKey(id)) === '1'
      : false;
  const sessionInvitesAlreadySent =
    Boolean(sessionInfo.inviteEmailsSentAt) || sessionInviteSkipFlag;
  const inviteableSessionMembers = orgMembers.filter((m) => m.id !== user?.id);

  const sessionInvitePanel =
    canManageSessionInvites && sessionInvitesAlreadySent ? (
      <div className="mb-3 rounded-lg border border-gray-200 p-4" data-tour="ps-invite-panel">
        <h2 className="text-sm font-semibold text-gray-900 mb-2">Resend email invites</h2>
        <p className="text-sm text-gray-500 mb-3">
          Invites were already sent when this practice was created (or on a previous resend). Use
          this only if someone did not receive the message. Resending may create duplicate calendar
          entries for recipients. Your account is not listed below; check &quot;Send invite to
          myself&quot; if you want another copy.
        </p>
        {inviteableSessionMembers.length === 0 ? (
          <p className="text-sm text-gray-500 mb-3">No other members in this organization.</p>
        ) : (
          <ul className="space-y-2 max-h-40 overflow-y-auto mb-3">
            {inviteableSessionMembers.map((m) => (
              <li key={m.id}>
                <label className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 rounded border-gray-300 text-green-700 focus:ring-green-600"
                    checked={inviteUserIds.includes(m.id)}
                    onChange={() => toggleSessionInviteMember(m.id)}
                  />
                  <span>
                    <span className="text-gray-900">{m.name || m.email}</span>
                    {m.name && <span className="block text-gray-500 text-xs">{m.email}</span>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <label className="flex items-start gap-2 cursor-pointer text-sm mb-3">
          <input
            type="checkbox"
            className="mt-0.5 rounded border-gray-300 text-green-700 focus:ring-green-600"
            checked={sendInviteToSelf}
            onChange={(e) => setSendInviteToSelf(e.target.checked)}
          />
          <span className="text-gray-800">Send invite to myself</span>
        </label>
        <button
          type="button"
          onClick={() => void handleSendSessionInvites()}
          disabled={inviteSending}
          className="bg-gray-800 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
        >
          {inviteSending ? 'Sending…' : 'Resend email invites'}
        </button>
      </div>
    ) : canManageSessionInvites && !sessionInvitesAlreadySent ? (
      <div className="mb-3 rounded-lg border border-gray-200 p-4" data-tour="ps-invite-panel">
        <h2 className="text-sm font-semibold text-gray-900 mb-2">Email invites</h2>
        <p className="text-sm text-gray-500 mb-3">
          Send calendar invites with a 15-minute reminder. Your account is not listed below; check
          &quot;Send invite to myself&quot; if you want a copy.
        </p>
        {inviteableSessionMembers.length === 0 ? (
          <p className="text-sm text-gray-500 mb-3">No other members in this organization.</p>
        ) : (
          <ul className="space-y-2 max-h-40 overflow-y-auto mb-3">
            {inviteableSessionMembers.map((m) => (
              <li key={m.id}>
                <label className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 rounded border-gray-300 text-green-700 focus:ring-green-600"
                    checked={inviteUserIds.includes(m.id)}
                    onChange={() => toggleSessionInviteMember(m.id)}
                  />
                  <span>
                    <span className="text-gray-900">{m.name || m.email}</span>
                    {m.name && <span className="block text-gray-500 text-xs">{m.email}</span>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <label className="flex items-start gap-2 cursor-pointer text-sm mb-3">
          <input
            type="checkbox"
            className="mt-0.5 rounded border-gray-300 text-green-700 focus:ring-green-600"
            checked={sendInviteToSelf}
            onChange={(e) => setSendInviteToSelf(e.target.checked)}
          />
          <span className="text-gray-800">Send invite to myself</span>
        </label>
        <button
          type="button"
          onClick={() => void handleSendSessionInvites()}
          disabled={inviteSending}
          className="bg-gray-800 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
        >
          {inviteSending ? 'Sending…' : 'Send email invites'}
        </button>
      </div>
    ) : null;

  const sessionToolbar =
    (canManageVisibility && !isCompleted) || showCopyLink ? (
      <div
        className="relative z-30 w-full mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 px-1"
        data-tour="ps-toolbar"
      >
        {canManageVisibility && (
          <div className="flex flex-col sm:items-end gap-1">
            <div className="flex items-center gap-2">
              <label htmlFor="session-visibility" className="text-sm text-gray-600 shrink-0">
                Teammate access
              </label>
              <select
                id="session-visibility"
                className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-0"
                value={sessionInfo.visibility ?? 'private'}
                disabled={visibilityUpdating || isCompleted}
                onChange={(e) =>
                  handleVisibilityChange(e.target.value === 'org_open' ? 'org_open' : 'private')
                }
              >
                <option value="private">Private</option>
                <option value="org_open">Organization — teammates can join</option>
              </select>
            </div>
            {visibilityError && (
              <p className="text-xs text-red-600 max-w-sm text-right" role="alert">
                {visibilityError}
              </p>
            )}
          </div>
        )}
        {showCopyLink && (
          <button
            type="button"
            onClick={() => void copySessionLink()}
            className="text-sm px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            {linkCopied ? 'Copied!' : 'Copy session link'}
          </button>
        )}
      </div>
    ) : null;

  const recordingAccessPanel =
    isCreatorForToolbar && isCompleted && id ? (
      <RecordingAccessControl
        sessionId={id}
        currentUserId={user?.id}
        recordingVisibility={sessionInfo.recordingVisibility ?? 'rep_only'}
        viewers={sessionInfo.viewers ?? NO_VIEWERS}
        onSaved={handleRecordingAccessSaved}
      />
    ) : null;

  if (sessionInfo.access === 'summary' && !isCompleted) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <p className="text-gray-700 mb-2">
            {sessionInfo.userName || sessionInfo.userEmail} is practicing with{' '}
            {sessionInfo.personaName}.
          </p>
          <RecordingPrivateNotice />
          <button
            onClick={() => navigate('/practice')}
            className="mt-4 block mx-auto text-sm text-gray-500 hover:text-gray-700"
          >
            Back to sessions
          </button>
        </div>
      </div>
    );
  }

  const sessionMain =
    sessionInfo.mode === 'verbal' ? (
      <VerbalSessionView {...sharedProps} onSendMessage={sendMessageSSE} />
    ) : (
      <TextSessionView {...sharedProps} onSendMessage={sendMessage} />
    );

  return (
    <>
      <div data-tour="ps-intro" className="h-0 w-0 overflow-hidden absolute" aria-hidden />
      {streamErrorBanner}
      {inviteEmailBanner}
      {inviteEmailSuccessBanner}
      {sessionInvitePanel}
      {sessionToolbar}
      {recordingAccessPanel}
      {sessionMain}
    </>
  );
}
